import React from "react";
import { MainDialog } from "../Dialgos/MainDialog";
import { useTranslation } from "react-i18next";
import { Info, MapPin, Phone, CreditCard, Tag } from "lucide-react";
import Visibility from "../utils/Visibility";
import { indexStore } from "src/store/indexStore";
import { Skeleton } from "../ui/skeleton";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { IMG_URl } from "src/common/img_url";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";
import { getStatusColor } from "./OrderRow";
import { formatDate } from "../utils/FormatDate";

function OrderDetails({ order_id }) {
  const { t } = useTranslation();
  const { orderLoading, fetchOrderFunction, order, nameLang } = indexStore();

  return (
    <MainDialog
      title={t("gloabl.order_details")}
      trigger={
        <div
          onClick={() => fetchOrderFunction(order_id)}
          className="flex text-sm gap-2 items-center cursor-pointer text-gray-600 bg-gray-200 p-2 rounded-full"
        >
          <Info /> <span>{t("gloabl.order_details")}</span>
        </div>
      }
    >
      {/* ====== Skeleton أثناء التحميل ====== */}
      <Visibility visible={orderLoading}>
        <Card className="w-full max-w-2xl mx-auto p-4 space-y-4 animate-pulse">
          {/* Header */}
          <div className="flex justify-between items-center">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-20" />
          </div>

          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-1/3" />

          {/* Delivery info */}
          <div className="space-y-2 border-t pt-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/4" />
          </div>

          {/* Items */}
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="flex justify-between items-center border-b pb-3"
              >
                <div className="flex gap-3">
                  <Skeleton className="h-16 w-16 rounded-md" />
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-4 w-10" />
              </div>
            ))}
          </div>
        </Card>
      </Visibility>

      {/* ====== المحتوى الفعلي بعد التحميل ====== */}
      <Visibility visible={!orderLoading}>
        {order && (
          <Card className="w-full max-w-2xl mx-auto p-4 space-y-4">
            {/* Header */}
            <CardHeader className="pb-2 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  {t("gloabl.order")} #{order.order_number}
                </h2>
                <div
                  className={`px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(
                    order.status
                  )}`}
                >
                  {t(`orderStatus.${order.status}`)}
                </div>
              </div>

              <div className="text-sm text-muted-foreground mt-2 space-y-1">
                <p>
                  {t("gloabl.created_at")}:{" "}
                  {formatDate(new Date(order.created_at))}
                </p>
                <p className="flex items-center gap-2">
                  {t("gloabl.total")} :
                  <span
                    dir="ltr"
                    className="font-semibold flex items-center gap-1 text-foreground"
                  >
                    <UAEDirhamIcon className="w-3 h-3" /> {order.total}
                  </span>
                </p>
              </div>
            </CardHeader>

            {/* Delivery Info */}
            {order.type === "delivary" && order.delivery_address && (
              <div className="border-b pb-3 space-y-2 text-sm">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {t("gloabl.delivery_address")}
                </h3>
                <p>
                  {order.delivery_address.address_line_1} -{" "}
                  {order.delivery_address.address_line_2}
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {order.delivery_address.phone}
                </p>
              </div>
            )}

            {/* Payment & Type */}
            <div className="border-b pb-3 flex flex-wrap gap-4 text-sm">
              <p className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                {t("gloabl.payment_method")}:{" "}
                <Badge variant="outline">
                  {t(`paymentMethod.${order.payment_method}`)}
                </Badge>
              </p>
              <p>
                {t("gloabl.type")}:{" "}
                <Badge variant="outline">{t(`orderType.${order.type}`)}</Badge>
              </p>
              {order.coupon && (
                <p className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {t("gloabl.copun") || "Coupon"}:{" "}
                  <Badge variant="outline" className="text-purple-600">
                    {order.coupon.code} - {order.coupon.value}
                    {order.coupon.type === "percentage" ? "%" : ""} (
                    {order.coupon.type === "percentage"
                      ? t("gloabl.percentage") || "Percentage"
                      : t("gloabl.fixed") || "Fixed"}
                    )
                  </Badge>
                </p>
              )}
              {order.note && (
                <p>
                  {t("gloabl.note")}:{" "}
                  <span className="italic text-muted-foreground">
                    {order.note}
                  </span>
                </p>
              )}
            </div>

            {/* Items */}
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="border-b pb-3 flex justify-between items-start"
                  >
                    <div className="flex gap-3">
                      <img
                        src={`${IMG_URl}/${item.product?.image}`}
                        alt="img"
                        className="object-cover rounded-md aspect-square w-16 h-16"
                      />
                      <div className="flex flex-col gap-1">
                        <h3 className="font-medium">
                          {item.product?.[nameLang] || "Unknown Product"}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {item.product?.[`description_${nameLang}`]}
                        </p>
                        <p
                          dir="ltr"
                          className="text-xs text-center flex items-center gap-1"
                        >
                          <UAEDirhamIcon className="w-3 h-3" />
                          {item.product?.price}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("gloabl.quantity")}: {item.quantity}
                        </p>

                        {/* Sub options */}
                        {item.sub_options?.length > 0 && (
                          <div className="text-xs mt-1 text-foreground space-y-1">
                            {item.sub_options.map((sub) => (
                              <div key={sub.id}>
                                *{sub.option?.[nameLang]} – {sub[nameLang]} (+
                                {sub.price})
                              </div>
                            ))}
                          </div>
                        )}

                        {item.note && (
                          <p className="text-xs italic text-muted-foreground mt-1">
                            {t("gloabl.note")} {item.note}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p
                        dir="ltr"
                        className="font-semibold flex items-center gap-1 text-foreground"
                      >
                        <UAEDirhamIcon className="w-3 h-3" /> {item.total_price}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>

            {/* Summary */}
            <div className="text-sm space-y-1 border-t pt-3">
              <p>
                {t("gloabl.total")}: {order.total}{" "}
                <UAEDirhamIcon className="inline w-3 h-3" />
              </p>
              {order.discount_value > 0 && (
                <p>
                  {t("gloabl.discount")}: -{order.discount_value}
                  <UAEDirhamIcon className="inline w-3 h-3" />
                </p>
              )}
              {order.delivary_fees > 0 && (
                <p className="flex items-center gap-1">
                  {t("gloabl.delivery_fees")}: {order.delivary_fees}
                  <UAEDirhamIcon className="inline w-3 h-3" />
                </p>
              )}
              <p className="font-semibold">
                {t("gloabl.total")}: {order.final_total || order.total}{" "}
                <UAEDirhamIcon className="inline w-3 h-3" />
              </p>
            </div>
          </Card>
        )}
      </Visibility>
    </MainDialog>
  );
}

export default OrderDetails;
